import { Spinner } from 'components/core';
import { submitServerChange } from 'components/dataCreationForms/common';
import { getCompaniesStoreState, useCompanies } from 'store/company';
import { useSites } from 'store/site';
import { toLabelledEntities } from 'helpers/misc';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useProject } from 'store/project';
import { companyOnViewToCompanyEditModel } from 'shared/domain/company/mapping';
import { companyClient } from 'shared/domain/company/companyClient';
import { CompanyOnView } from 'shared/domain/company/types/view';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { Identificable } from 'shared/types/commonView';
import { companyModelToCompanyOnView } from 'views/companies/mapping';
import { EditCompanyContext } from '../types';
import { handleFormValidation, validate } from '../validation';
import {
  findCompanyModelForEditing,
  shouldDisableInvolvement,
} from './model';
import { useAppNavigation } from 'contexts/navigation';

const formName = 'company';
const SUBMIT_EVENT_TYPE = 'submit-company';
const WithEditCompanyContext = createContext<
  EditCompanyContext | undefined
>(undefined);

function WithEditCompany({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const { goToCompanies } = useAppNavigation();
  const intl = useIntl();
  const { organizationId, _id: projectId } = useProject();

  const [isPosting, setIsPosting] = useState<boolean>(false);
  const availableSites = toLabelledEntities(sites.filter(keepUndeleted));
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const {
    items: companies,
    loading: loadingCompanies,
  } = useCompanies();
  const currentCompanyId = useAppUrlParams(UrlParamName.COMPANY_ID);

  const [existingValues, setExistingValues] = useState<
    CompanyOnView | undefined
  >(undefined);
  const [disableInvolvement, setDisableInvolvement] =
    useState<boolean>(false);

  useEffect(() => {
    const existingCompanyModel = findCompanyModelForEditing(
      companies,
      currentCompanyId
    );
    let companyOnView;
    if (existingCompanyModel) {
      companyOnView = companyModelToCompanyOnView(
        existingCompanyModel,
        // contracts,
        //TODO should be availableSites only
        sites,
        intl,
        organizationId
      );
      setExistingValues(companyOnView);
    }
    setDisableInvolvement(
      shouldDisableInvolvement(companyOnView, companies)
    );
  }, [companies, sites, intl, currentCompanyId]);

  const [open, setOpen] = useState(false);

  const openDialog = useCallback(() => {
    setOpen(true);
  }, []);

  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );

  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: CompanyOnView) => {
      const companyEditModel = companyOnViewToCompanyEditModel(values, projectId);
      const { saved } = await submitServerChange({
        change: () => companyClient.edit(companyEditModel),
        setOpen,
        setIsPosting,
        action: 'updating a company',
      });
      if (!saved) {
        return;
      }

      // Back to the list (a company has no separate details view), refreshed first.
      await getCompaniesStoreState().actions.reload();
      goToCompanies();
    },
    [goToCompanies, projectId]
  );

  const ctx: EditCompanyContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      existingValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate,
      handleFormValidation,
      disableInvolvement,
      availableSites,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    existingValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    disableInvolvement,
    availableSites,
    onSuccess,
  ]);
  if (loadingCompanies || loadingSites) {
    return (
      <Spinner
        reason={`CompanyWizard loadingCompanies: ${loadingCompanies} || loadingSites: ${loadingSites} || existingValues: ${existingValues}`}
      />
    );
  }
  return (
    <WithEditCompanyContext.Provider value={ctx}>
      {children}
    </WithEditCompanyContext.Provider>
  );
}

function useEditCompany(): EditCompanyContext {
  const context = useContext(WithEditCompanyContext);
  if (context === undefined) {
    throw new Error(
      'useEditCompany must be used within an EditCompanyContext'
    );
  }
  return context;
}

const withEditCompany =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithEditCompany>
      <Component {...props} />
    </WithEditCompany>
  );

export { WithEditCompany, useEditCompany, withEditCompany };
